'use client';

import { useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { useParams } from 'next/navigation';
import { type JSX, useState } from 'react';

import { usePaginationPostsQuery } from '~/shared/apollo-queries';
import BreadCrumbs from '~/shared/components/bread-crumbs';
import ContactsRequestForm from '~/shared/components/contacts-request-form';
import Content from '~/shared/components/content';
import ItemsNavigator from '~/shared/components/items-navigator';
import NavigationArrows from '~/shared/components/navigation-arrows';
import { getPageConfig } from '~/shared/configs/pages/get-page-config';
import { CONSULTATION_TYPE, LIMIT_FOR_FETCHING_ITEMS, LIST_ITEM_TYPE, PAGE_NAMES } from '~/shared/constants';
import { findOneTagQuery } from '~/shared/graphql-queries/tags/find-one-tag-query';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { paginatePublicPosts } from '~/shared/helpers/public-posts';
import type { PostType } from '~/shared/types/post-type';
import { getUrl } from '~/shared/helpers/string-helpers';
import { useTypedTranslation } from '~/shared/hooks';

import './posts-tags.scss';

type PostsTagsPropsType = {
  lng: string | null;
  perPage?: number;
  withNoForm?: boolean;
  withoutBreadcrumbs?: boolean;
  withNoTitle?: boolean;
  withLoadMore?: boolean;
  tagAlias?: string;
  initialPosts?: PostType[];
  initialTag?: { name: string; alias: string };
};

const PostsTags = ({
  lng,
  perPage,
  withNoForm = false,
  withoutBreadcrumbs = false,
  withNoTitle,
  withLoadMore = true,
  tagAlias,
  initialPosts,
  initialTag,
}: PostsTagsPropsType): JSX.Element => {
  const [nextPage, setNextPage] = useState(1);
  const t = useTypedTranslation(['button']);
  const [isNameSortDesc, setIsNameSortDesc] = useState(null);
  const [isDateSortDesc, setIsDateSortDesc] = useState(true);
  const params = useParams();
  const {
    error,
    data: queryData,
    fetchMore: queryFetchMore,
  } = usePaginationPostsQuery(
    {
      filter: { tagAlias: tagAlias ?? params?.alias, language: lng },
      perPage,
      nextPage,
    },
    {
      skip: initialPosts !== undefined,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    true,
    isDateSortDesc
      ? 'CREATED_AT_DESC'
      : isDateSortDesc === false
        ? 'CREATED_AT_ASC'
        : isNameSortDesc
          ? 'TITLE_DESC'
          : 'TITLE_ASC',
  );
  const pageConfig = getPageConfig({ t, path: PAGE_NAMES.POSTS_TAGS });

  const { data: tagData } = useQuery<{
    findOneTagQuery: { name: string; alias: string };
  }>(findOneTagQuery, {
    variables: { filter: { alias: tagAlias ?? params?.alias } },
    skip: Boolean(initialTag) || withoutBreadcrumbs || !(tagAlias ?? params?.alias),
  });
  const tag = initialTag ?? tagData?.findOneTagQuery;
  const sort = isDateSortDesc
    ? 'CREATED_AT_DESC'
    : isDateSortDesc === false
      ? 'CREATED_AT_ASC'
      : isNameSortDesc
        ? 'TITLE_DESC'
        : 'TITLE_ASC';
  const postPaginationData =
    initialPosts !== undefined
      ? {
          paginationPostQuery: paginatePublicPosts(initialPosts, nextPage, perPage ?? LIMIT_FOR_FETCHING_ITEMS, sort),
        }
      : queryData;
  const fetchMore = async (variables: { nextPage?: number; sort?: string }) => {
    if (initialPosts !== undefined) {
      setNextPage(variables.nextPage ?? 1);
      return;
    }
    try {
      await queryFetchMore(variables);
    } catch (error) {
      console.warn('Posts unavailable', error);
    }
  };

  return (
    <>
      <Content isPageContent>
        {!withoutBreadcrumbs && (
          <BreadCrumbs
            className="posts-tags__breadcrumbs"
            t={t}
            links={[
              {
                name: t('text.prana-club'),
                href: getLocaleLink(PAGE_NAMES.HOME, lng),
              },
              {
                name: t('page.blog.title'),
                href: getLocaleLink(PAGE_NAMES.BLOG, lng),
              },
              ...(tag?.alias
                ? [
                    {
                      name: tag?.name,
                      href: getLocaleLink(
                        getUrl({
                          link: PAGE_NAMES.POSTS_TAGS,
                          alias: tag?.alias,
                        }),
                        lng,
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
        <section className="posts-tags__header-wrapper">
          <h1 className={classNames('posts-tags__short-description-label', 'posts-tags__title')}>
            {t('text.same-posts')}
          </h1>
          <div className="posts-tags__navigation-arrows">
            <NavigationArrows
              hasPreviousPage={postPaginationData?.paginationPostQuery?.pageInfo?.hasPreviousPage}
              hasNextPage={postPaginationData?.paginationPostQuery?.pageInfo?.hasNextPage}
              onPreviousPageClick={() => setNextPage(nextPage - 1)}
              onNextPageClick={() => setNextPage(nextPage + 1)}
            />
          </div>
        </section>
        <ItemsNavigator
          withLoadMore={withLoadMore}
          lng={lng}
          getTagLink={(data) => getLocaleLink(getUrl({ link: PAGE_NAMES.POSTS_TAGS, alias: data?.alias }), lng)}
          withSortBar={false}
          withPagination={false}
          isMobile={false}
          loading={false}
          itemType={LIST_ITEM_TYPE.EXTENDED}
          pageToGo={PAGE_NAMES.POST}
          error={initialPosts !== undefined || queryData?.paginationPostQuery?.items?.length ? undefined : error}
          data={postPaginationData?.paginationPostQuery}
          t={t}
          fetchMore={fetchMore}
        />
      </Content>
      {!withNoForm && (
        <section className="posts-tags__contacts-request-form">
          <ContactsRequestForm type={CONSULTATION_TYPE.ZERO_LESSON} t={t} />
        </section>
      )}
    </>
  );
};

export default PostsTags;
